import { browserHistory } from 'utils/navigation'
import request, { generateNotyMessage } from 'utils/request'
import { MODAL_BUTTONS_MAP, MODAL_CLASSES_MAP, MODAL_CONTENT_MAP, REDIRECT_DELAY } from 'utils/constants'

import { modalActions } from 'store/modal'
import { sendingActions } from 'store/sender'
import { actions as notyActions } from 'layouts/ErrorBox'
import { getParticipantErrors } from '../../../../utils/participantValidation'
import { _fetchPayments, _fetchTreasureChestPayoutHistory } from '../../../../../Payments/modules/Payments'
import { actions as ptpActions } from '../../../ParticipantsPage/modules/Participants'
import { getStudyVersionID } from '../../../../../../../../../../utils/studyConfigUtils'

export const FORM_FIELDS = [
  {
    key: 'default_locale',
    text: 'Locale',
    type: 'select',
    // options for the dropdown will be populated by the either the '/locales' API endpoint or list of enforced languages if available,
    // not hard coded here to allow for more locales as they come
  },
  {
    key: 'fname',
    text: 'Preferred Name',
    type: 'text',
    placeholder: 'Preferred Name',
    tester: true,
  },
  {
    key: 'lname',
    text: 'Last Name',
    type: 'text',
    placeholder: 'Last Name',
    tester: true,
  },
  {
    key: 'subject_id',
    text: 'Subject ID',
    type: 'text',
    placeholder: 'Subject ID',
    tester: true,
  },
]

const DEFAULT_CAREGIVER_DATA_FIELDS_CONFIG = {
  mandatory: [
    {
      display_label: 'Sites',
      type: 'number',
      metadata: {
        internal_key: 'site_id',
        ui_type: 'custom_dropdown',
        custom_validations: '',
        is_custom: false,
        default_value: null,
        can_update: true,
      },
    },
    {
      display_label: 'Subject ID',
      type: 'text',
      metadata: {
        internal_key: 'subject_id',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Locale',
      type: 'text',
      metadata: {
        internal_key: 'default_locale',
        custom_validations: '',
        is_custom: false,
        default_value: 'en_US',
        can_update: true,
      },
    },
  ],
  optional: [
    {
      display_label: 'Preferred Name',
      type: 'text',
      metadata: {
        internal_key: 'fname',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Last Name',
      type: 'text',
      metadata: {
        internal_key: 'lname',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
  ],
  disabled: [],
}

const regularDataValidation = {
  fname: 'fname',
  subject_id: 'subject_id',
  default_locale: 'default_locale',
}

//
// Actions
//

const UPDATE_FIELD = 'UPDATE_FIELD'
const RESET_SUBJECT_ID_STATUS = 'RESET_SUBJECT_ID_STATUS'

export function saveToDatabase({
  studyID,
  siteID,
  participant: caregiver,
  checkedCohorts: _checkedCohorts,
  participantID,
  isPatch = false,
  sitesArr,
  pDataFields: cgDataFields,
  participantField,
  hideSuccessMessage = false,
  armId,
}) {
  let currentSite
  return (dispatch, getState) => {
    const { drawer, location, study } = getState()
    const studyVersionID = getStudyVersionID(location)
    const hasStudyPromotion = !!study?.currentStudy?.config?.study_promotion

    if (
      !validateCaregiver({
        participant: caregiver,
        dispatch,
        sitesArr,
        pDataFields: cgDataFields,
        isPatch,
      })
    ) {
      return false
    }

    dispatch(modalActions.closeModal())
    const cgCopy = { ...caregiver }
    currentSite = sitesArr.shift()
    cgCopy.study_id = studyID
    cgCopy.site_id = currentSite
    cgCopy.is_caregiver = true

    const hasDisabledCgFields = cgDataFields?.disabled
    if (cgCopy.visit_start_datetime === '') delete cgCopy.visit_start_datetime
    if (hasDisabledCgFields) {
      hasDisabledCgFields.forEach(data => {
        delete cgCopy[data.metadata.internal_key]
      })
    }
    if (isPatch && cgDataFields) {
      const { mandatory, optional, disabled } = cgDataFields
      const cantUpdateArr = [...mandatory, ...optional, ...disabled].filter(field => {
        return !(field.metadata.can_update ?? true)
      })
      cantUpdateArr.forEach(field => {
        delete cgCopy[field.metadata.internal_key]
      })
    }

    const successMessage = !hideSuccessMessage ? `Successfully ${isPatch ? 'edited' : 'added'} caregiver` : undefined

    if (hasStudyPromotion && studyVersionID) {
      cgCopy.study_version_id = studyVersionID
    }

    dispatch(sendingActions.startSender())
    return dispatch(
      request({
        method: isPatch ? 'PATCH' : 'POST',
        successMessage,
        url: `/control/${isPatch ? `studies/${studyID}/sites/${siteID}/participants/${participantID}` : 'caregivers'}`,
        failMessage: 'Error saving caregiver',
        body: JSON.stringify(cgCopy),
        success: response => {
          delete caregiver.password
          dispatch(sendingActions.stopSender())
          if (
            participantField &&
            typeof caregiver[participantField] !== 'undefined' &&
            caregiver[participantField] !== null
          )
            dispatch(updateField(participantField, caregiver[participantField]))
          if (drawer.open && participantField === 'opt_out') dispatch(ptpActions.fetchPtps(studyID))
          setTimeout(() => {
            browserHistory.push(`/studies/${studyID}/${studyVersionID}/participants`)
            dispatch(sendingActions.resetSender())
          }, REDIRECT_DELAY)
        },
        fail: (res, content) => {
          dispatch(notyActions.showError({ text: generateNotyMessage(content.message, false) }))
          dispatch(sendingActions.resetSender())
        },
        hasLoader: true,
        loadingKey: 'request',
        hasSender: true,
      }),
    )
  }
}

function updateField(field, value) {
  return dispatch => {
    dispatch({
      type: UPDATE_FIELD,
      field,
      value,
    })
  }
}

const resetSubjectIdStatus = () => {
  return {
    type: RESET_SUBJECT_ID_STATUS,
  }
}

export function validateCaregiver({
  participant,
  dispatch,
  pDataFields = DEFAULT_CAREGIVER_DATA_FIELDS_CONFIG,
  isPatch,
  isVisitCycleSupported = false,
}) {
  const { fname, subject_id, default_locale } = participant
  let errorMessage = ''
  const mandatoryPtpFields = [...pDataFields?.mandatory]

  const defaultValidationKeys = Object.keys(regularDataValidation)

  let mandatoryValidations =
    mandatoryPtpFields?.length > 0
      ? defaultValidationKeys.filter(key => mandatoryPtpFields.map(data => data.metadata.internal_key).includes(key))
      : defaultValidationKeys

  /**
   * We will create a map of custom validations if any field has any
   * custom validation (e.g. regex pattern the value must meet)
   */
  const customValidationsMap = mandatoryPtpFields.reduce((acc, currentField) => {
    const { metadata } = currentField
    const { custom_validations, internal_key } = metadata
    const obj = { ...acc }
    if (custom_validations) {
      obj[internal_key] = custom_validations
    }
    return obj
  }, {})

  const errors = getParticipantErrors({
    participantData: {
      fname,
      subject_id,
      default_locale,
    },
    mandatoryValidations,
    customValidationsMap,
    isVisitCycleSupported,
  })
  if (errors && errors.length > 0) {
    errorMessage += `${errors.join('<br>')}<br>`
  }
  if (errorMessage !== '' && dispatch) {
    dispatch(notyActions.showError({ text: generateNotyMessage(errorMessage, false) }))
  }
  return errorMessage === ''
}

function openSaveModal({
  studyID,
  siteID,
  participant,
  checkedCohorts: _checkedCohorts,
  participantID,
  consentRequired,
  sites,
  pDataFields,
  isNotLeaving,
  ptpField,
  hasVisitSupport,
  redirect = null,
  hideSuccessMessage,
  armId,
}) {
  const _sitesArr = Object.keys(sites)
  const hasSites = _sitesArr.length > 0 && _sitesArr[0] !== 'null'
  const sitesArr = hasSites ? _sitesArr.map(idString => parseInt(idString, 10)) : []
  return dispatch => {
    if ('id' in participant) {
      return dispatch(
        saveToDatabase({
          studyID,
          siteID,
          participant,
          checkedCohorts: _checkedCohorts,
          participantID,
          isPatch: true,
          sitesArr,
          pDataFields,
          isNotLeaving,
          participantField: ptpField,
          hasVisitSupport,
          hideSuccessMessage,
          armId,
        }),
      )
    }
    const onConfirm = () => {
      dispatch(
        saveToDatabase({
          studyID,
          siteID,
          participant,
          checkedCohorts: _checkedCohorts,
          participantID: null,
          isPatch: false,
          sitesArr,
          pDataFields,
          isNotLeaving,
          hasVisitSupport,
          redirect,
          hideSuccessMessage,
          armId,
        }),
      )
    }
    if (consentRequired) {
      onConfirm()
    } else {
      dispatch(
        modalActions.openModal({
          content: MODAL_CONTENT_MAP.addStudyParticipant,
          confirmButton: MODAL_BUTTONS_MAP.confirm,
          cancelButton: MODAL_BUTTONS_MAP.cancel,
          className: MODAL_CLASSES_MAP.manualConsentConfirmation,
          onConfirm,
        }),
      )
    }
  }
}

export const actions = {
  saveToDatabase: openSaveModal,
  updateField,
  openModal: modalActions.openModal,
  closeModal: modalActions.closeModal,
  resetSubjectIdStatus,
}
